import { useEffect, useRef, useState } from 'react'

export interface ColumnVisibilityDef {
  id: string
  label: string
  alwaysVisible?: boolean
  hiddenByDefault?: boolean
}

export function useColumnVisibility(
  storageKey: string,
  columns: ColumnVisibilityDef[],
) {
  const [open, setOpen] = useState(false)
  const [hiddenCols, setHiddenCols] = useState<Set<string>>(() => {
    if (typeof window === 'undefined') return new Set()
    try {
      const stored = localStorage.getItem(storageKey)
      if (stored) {
        const parsed = JSON.parse(stored)
        if (Array.isArray(parsed)) {
          return new Set(
            parsed.filter(
              (id): id is string =>
                typeof id === 'string' && columns.some((col) => col.id === id),
            ),
          )
        }
      }
      return new Set(
        columns
          .filter(
            (column) =>
              !column.alwaysVisible && column.hiddenByDefault === true,
          )
          .map((column) => column.id),
      )
    } catch {
      return new Set(
        columns
          .filter(
            (column) =>
              !column.alwaysVisible && column.hiddenByDefault === true,
          )
          .map((column) => column.id),
      )
    }
  })

  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (typeof window === 'undefined') return
    const values = [...hiddenCols]
    localStorage.setItem(storageKey, JSON.stringify(values))
  }, [hiddenCols, storageKey])

  useEffect(() => {
    if (!open) return
    const handler = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const isVisible = (id: string) => {
    const column = columns.find((col) => col.id === id)
    if (!column) return true
    if (column.alwaysVisible) return true
    return !hiddenCols.has(id)
  }

  const toggleColumn = (id: string) => {
    const column = columns.find((col) => col.id === id)
    if (!column || column.alwaysVisible) return
    setHiddenCols((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const hiddenCount = columns.filter(
    (col) => !col.alwaysVisible && hiddenCols.has(col.id),
  ).length

  return {
    ref,
    open,
    setOpen,
    hiddenCols,
    hiddenCount,
    isVisible,
    toggleColumn,
  }
}
